import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
// eslint-disable-next-line no-restricted-imports
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import * as sdk from '@d-id/client-sdk';
import { LoginService } from '@shared-angular/services/login.service';
import { applyChromaKey, startChromaKeyVideoLoop } from '@shared-angular/utils/chroma-key.util';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';
import { BotDebugComponent } from 'src/app/shared/components/ai-bot/bot-debug/bot-debug.component';
import { BotPromptComponent } from 'src/app/shared/components/ai-bot/utils/bot-prompt/bot-prompt.component';
import { DisconnectOverlayComponent } from 'src/app/shared/components/disconnect-overlay/disconnect-overlay.component';
import { MicPermissionOverlayComponent } from 'src/app/shared/components/mic-permission-overlay/mic-permission-overlay.component';
import { AudioToTextService } from 'src/app/shared/services/audio-to-text/audio-to-text.service';
import { BotMessagesService } from 'src/app/shared/services/bot-messages.service';
import { BotService } from 'src/app/shared/services/bot.service';
import { EventManagerService } from 'src/app/shared/services/event-manager/event-manager.service';
import { IframeCommunicationService } from 'src/app/shared/services/iframe-communication/iframe-communication.service';
import { OverlayManagerService } from 'src/app/shared/services/overlay-manager.service';
import { PlayAudioService } from 'src/app/shared/services/playaudio.service';
import { VideoAvatarManagerService } from 'src/app/shared/services/ragai/video-avatar-manager.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { SpeechService } from 'src/app/shared/services/speech.service';
import { TurnLatencyService } from 'src/app/shared/services/turn-latency/turn-latency.service';
import { StatisticService } from '../admin/tabs/insights/statistics-tab/statistic.service';
import { AudioDebuggerComponent } from '../audionew/audio-debugger/audio-debugger.component';
import { AvatarFullscreenFooterButtonsComponent } from '../avatar/avatar-fullscreen-footer-buttons/avatar-fullscreen-footer-buttons.component';
import { AvatarFullscreenHeaderComponent } from '../avatar/avatar-fullscreen-header/avatar-fullscreen-header.component';
import { VoiceBarsComponent } from '../avatar/voice-bars/voice-bars.component';
import { BotBasedComponent } from '../bot-based/bot-based.component';

@Component({
    styleUrl: './did-avatar.component.scss',
    templateUrl: './did-avatar.component.html',
    imports: [NgClass, NgStyle, NgTemplateOutlet, FormsModule, BotPromptComponent, DisconnectOverlayComponent, MicPermissionOverlayComponent, AudioDebuggerComponent, BotDebugComponent, VoiceBarsComponent, AvatarFullscreenHeaderComponent, AvatarFullscreenFooterButtonsComponent],
    providers: [BsModalService]
})
export class DIdAvatarComponent extends BotBasedComponent implements OnInit, OnDestroy {
    @ViewChild('videoElement') private videoElement!: ElementRef<HTMLVideoElement>;
    @ViewChild('videoWrapper') private videoWrapper!: ElementRef<HTMLDivElement>;
    @ViewChild('backgroundImage') private backgroundImageEl!: ElementRef<HTMLDivElement>;
    @ViewChild('canvasElement') private canvasElement!: ElementRef<HTMLCanvasElement>;
    @ViewChild('fullscreenContent') private fullscreenContentEl?: ElementRef<HTMLDivElement>;

    public viewState: 'INITIAL' | 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' = 'INITIAL';
    public currentDebugState: 'none' | 'overlay' | 'fullsize' = 'none';
    public myWords: string = '';
    public botWords: string = '';
    public hasMsgBeenSend = false;
    public mediaWidth = 0;
    public mediaHeight = 0;
    public backgroundUrl: string = '';

    private agentManager: sdk.AgentManager | null = null;
    private wasSpeaking = false;
    private aspectRatio = 16 / 9;
    private readonly minVideoWidth = 400;
    private readonly minVideoHeight = 250;
    public isVirtualBgRunning = false;
    private virtualBgAnimationFrameId: number | null = null;
    private virtualBgRetryCount = 0;
    private readonly maxVirtualBgRetries = 40;
    private virtualBgRetryTimeoutId: number | null = null;
    private turnLatency = inject(TurnLatencyService);

    // Im Fullscreen-Modus ist Greenscreen immer aktiv, unabhaengig vom (dort disabled) useGreenscreen-Schalter.
    public get effectiveGreenscreen(): boolean {
        const config = this.botConfig.didAvatarConfig;
        return !!config?.useGreenscreen || !!config?.fullscreenConfig?.active;
    }

    public get fullscreenActive(): boolean {
        return !!this.botConfig.didAvatarConfig?.fullscreenConfig?.active;
    }

    @HostListener('window:resize')
    public onResize(): void {
        this.resizeDiv();
    }

    constructor(
        public override route: ActivatedRoute,
        public override loginService: LoginService,
        public override speechService: SpeechService,
        public override statisticsService: StatisticService,
        public override iframeCommunication: IframeCommunicationService,
        public override playService: PlayAudioService,
        public override bot: BotService,
        public override socketService: SocketService,
        public override toastr: ToastrService,
        public override modalService: BsModalService,
        public override domSanatizer: DomSanitizer,
        protected override botMessages: BotMessagesService,
        public override logger: RagLoggerService,
        public attService: AudioToTextService,
        private http: HttpClient,
        private eventManager: EventManagerService,
        private videoAvatarManager: VideoAvatarManagerService,
        protected override overlayManager: OverlayManagerService
    ) {
        super(route, loginService, speechService, statisticsService, iframeCommunication, playService, bot, socketService, toastr, modalService, domSanatizer, botMessages, logger, overlayManager);
        this.showMcpApprovalOverlayEnabled = true;
        this.backgroundUrl = this.botConfig.didAvatarConfig?.backgroundUrl ?? '';
        this.iframeCommunication.RAGAI.WAITFOR._requireAvatarForInitialIdle();
    }

    protected get muteButtonLabel(): string {
        return this.attService.isMuted
            ? $localize`:@@audio-unmute:Stummschaltung aufheben`
            : $localize`:@@audio-mute:Stummschalten`;
    }

    public override async ngOnInit(): Promise<void> {
        await super.ngOnInit();
        this.injectCSS(this.botConfig._id, 'did-avatar');
        this.resizeDiv();
        this.turnLatency.enable('did', this.botConfig._id);

        // Barge-in-Dispatch: VideoAvatarManagerService.stop() kennt den Provider nicht selbst,
        // daher hier der providerspezifische Interrupt-Call ueber das D-ID-SDK.
        this.eventManager.on('RAGAI.AVATAR.INTERRUPT', () => {
            if (this.agentManager?.getIsInterruptAvailable()) {
                this.agentManager.interrupt({ type: 'manual' });
            }
        });

        this.subscriptions.push(
            // Mikrofon-Eingabe → Nachricht senden
            this.attService.textReceived.subscribe(async (text) => {
                this.myWords = text;
                if (this.attService.lastVoiceTiming) this.turnLatency.startVoiceTurn(this.attService.lastVoiceTiming);
                await this.add(text);
            }),

            // Hintergrundbild wurde per Blockly-Block (AVATAR_setBGImg/unsetBGImg) geändert
            this.videoAvatarManager.onBackgroundUpdate.subscribe((keyValEntry) => {
                this.backgroundUrl = keyValEntry.value as string;
            }),
            this.videoAvatarManager.onBackgroundDelete.subscribe(() => {
                this.backgroundUrl = this.botConfig.didAvatarConfig?.backgroundUrl ?? '';
            }),

            // Aufnahme gestartet → Bot-Denken abbrechen
            this.attService.recStarted.subscribe(async (started) => {
                if (started) {
                    await this.cancelThinking();
                }
            }),

            // Bot antwortet (Streaming) → Mic-Threshold erhöhen
            this.socketService.onRunUpdate.subscribe(() => {
                this.attService.speakingState.next(true);
                this.attService.speechRecognitionThresholdElevationMultiplyer = 2;
            }),

            // Bot-Antwort fertig → Text merken (MCP-Rollen ignorieren, keine Sprachausgabe dafuer)
            this.socketService.onRunComplete.subscribe((event) => {
                if (event.role !== 'assistant') return;
                this.botWords = event.content
                    .filter(c => c.type === 'text')
                    .map(c => (c).text)
                    .join(' ');
            }),

            // D-ID Speak: Server sendet Text → SDK speak aufrufen
            this.socketService.onDidSpeak.subscribe((data) => {
                if (this.agentManager && data.text?.trim()) {
                    console.info("send speak with text", data.text);
                    void this.agentManager.speak({ type: 'text', input: data.text });
                }
            }),

            // State-Update → thinking-Flag + Auto-Mic bei Idle
            this.socketService.onStateUpdate.subscribe((data) => {
                this.isThinking = data.sessionState === 'thinking';
                if (data.sessionState === 'thinking') {
                    this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.thinking', {});
                }
                if (data.sessionState === 'idle') {
                    this.attService.speakingState.next(false);
                    this.attService.speechRecognitionThresholdElevationMultiplyer = 1;
                    // Mikrofon ist immer verbunden (siehe connectDIdAvatar) - nur neu starten, Mute-Status bleibt unveraendert
                    this.attService.startRecording();
                }
            })
        );
    }

    public async connectDIdAvatar(): Promise<void> {
        this.viewState = 'CONNECTING';
        try {
            // Get agentId + clientKey from server
            const { agentId, clientKey } = await firstValueFrom(
                this.http.post<{ agentId: string; clientKey: string }>(
                    `/api/toolbox/did/session/${this.botConfig._id}`, {}
                )
            );

            // Create SDK agent manager with callbacks
            const callbacks: Parameters<typeof sdk.createAgentManager>[1]['callbacks'] = {
                onSrcObjectReady: (value: MediaStream) => {
                    console.log("SRC Object Ready");

                    this.videoElement.nativeElement.srcObject = value;
                    this.turnLatency.attachAudio(value);
                },
                onVideoStateChange: (state: string) => {
                    console.log("Vieo State changed", state);
                    // Fluent-Streaming: ein durchgehender WebRTC-Stream fuer Idle UND Sprechen, kein
                    // Umschalten zwischen .src/.srcObject mehr noetig (srcObject bleibt dauerhaft aus
                    // onSrcObjectReady gesetzt).
                },
                // Einzig verlaessliches Signal fuer "spricht der Avatar gerade wirklich" - onVideoStateChange
                // (START/STOP) feuert bereits fuer das Warmup-/Idle-Loop-Video und ist dafuer ungeeignet.
                onAgentActivityStateChange: (state: string) => {
                    console.log("Agent activity state changed", state);
                    if (state === 'TALKING') {
                        this.turnLatency.markProviderSpeakStart();
                        this.attService.speakingState.next(true);
                        this.attService.speechRecognitionThresholdElevationMultiplyer = 2;
                        if (!this.wasSpeaking) {
                            this.wasSpeaking = true;
                            this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.play', {});
                        }
                    } else if (state === 'IDLE') {
                        this.attService.speakingState.next(false);
                        this.attService.speechRecognitionThresholdElevationMultiplyer = 1;
                        // Mikrofon ist immer verbunden (siehe connectDIdAvatar) - nur neu starten, Mute-Status bleibt unveraendert
                        this.attService.startRecording();
                        if (this.wasSpeaking) {
                            this.wasSpeaking = false;
                            this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.done', {});
                        }
                    }
                },
                onConnectionStateChange: (state: string) => {
                    console.log("onConnectionStateChange", state);

                    if (state === 'connected') {
                        this.viewState = 'CONNECTED';
                        console.log('[DIdAvatarComponent] Stream type:', this.agentManager?.getStreamType());
                        this.iframeCommunication.RAGAI.WAITFOR._avatarConnected();
                        // Mikrofon verbindet sich immer; voiceInputActive entscheidet nur, ob es sofort
                        // unmuted zuhoert oder zunaechst stummgeschaltet bleibt (manuell per Mute-Button aktivierbar).
                        this.attService.isMuted = !this.botConfig.didAvatarConfig?.voiceInputActive;
                        this.attService.state.next('connecting');
                        this.attService.startRecording();
                        setTimeout(() => {
                            this.eventManager.triggerEventHook('io_out_registerAvatarSession', [{ sessionId: this.botConfig._id }]);
                            this.resizeDiv();
                            this.tryStartVirtualBackground();
                            // Direkt nach Verbindungsaufbau gilt der Avatar als "fertig gesprochen" (zuhoerend) -
                            // ohne diesen Trigger bleibt der per Blockly gesetzte Statustext bis zur ersten
                            // Sprachausgabe leer und es wird der eingebaute Standardtext angezeigt. Verzoegert
                            // per Makrotask, da RAGAI.WAITFOR._avatarConnected() (oben) das Registrieren der
                            // Blockly-Listener selbst erst ueber einen await-Ketten-Umweg anstoesst - ohne
                            // Makrotask-Grenze koennte dieser Trigger vor der Listener-Registrierung feuern.
                            this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.done', {});
                            // Feuert bei JEDEM Connect (auch Reconnect) - im Gegensatz zu RAGAI.WAITFOR.initialIdle(),
                            // das nur beim allerersten Verbindungsaufbau der Seite aufloest (siehe WaitForManagerService).
                            this.eventManager.triggerEventHook('RAGAI.AVATAR.CONNECTED', {});
                        }, 100);
                    } else if (state === 'disconnected' || state === 'closed' || state === 'fail') {
                        this.stopVirtualBackground();
                        this.videoAvatarManager.clearFooterButtons();
                        this.viewState = 'INITIAL';
                        this.agentManager = null;
                    }
                },
                onError: (error: Error, errorData?: object) => {
                    console.error('[D-ID SDK] Error:', error, errorData);
                },
                // Rein additiv fuer das Kosten-Tracking (siehe plan-did-cost-tracking.md): ordnet die
                // Session sofort unserem Mandanten/Bot zu, kein Einfluss auf Video/Audio-Pipeline.
                onStreamCreated: (stream: { stream_id: string; session_id: string; agent_id: string }) => {
                    this.http.post(`/api/toolbox/did/session-created/${this.botConfig._id}`, {
                        sessionId: stream.session_id,
                        agentId: stream.agent_id,
                    }).subscribe({ error: (err) => console.warn('[DIdAvatarComponent] session-created report failed:', err) });
                }
            };

            this.agentManager = await sdk.createAgentManager(agentId, {
                auth: { type: 'key', clientKey },
                callbacks,
                // D-ID's Referenz-Implementierung setzt fluent immer zusammen mit compatibilityMode
                // + streamWarmup (siehe agents-sdk-demo) - ohne diese liefert der Stream keine Frames.
                streamOptions: { fluent: true, compatibilityMode: 'on', streamWarmup: true }
            });

            await this.agentManager.connect();
        } catch (err) {
            console.error('[DIdAvatarComponent] connect error:', err);
            if (err instanceof HttpErrorResponse && err.status === 400 && (err.error as { error?: string })?.error) {
                this.toastr.error('ERROR', (err.error as { error?: string }).error ?? '');
            } else {
                this.toastr.error('ERROR_500', $localize`:@@did-avatar-connect-error:Die Verbindung zum Avatar ist fehlgeschlagen. Versuche es erneut.`);
            }
            this.videoAvatarManager.clearFooterButtons();
            this.viewState = 'INITIAL';
        }
    }

    public async disconnectDIdAvatar(): Promise<void> {
        this.stopVirtualBackground();
        this.videoAvatarManager.clearFooterButtons();
        this.attService.setMuted(true);
        if (this.agentManager) {
            await this.agentManager.disconnect();
            this.agentManager = null;
        }
        await firstValueFrom(
            this.http.delete(`/api/toolbox/did/session/${this.botConfig._id}`)
        ).catch(() => { });
        this.viewState = 'INITIAL';
    }

    // add() erhält den Text als nonPromtInput-Argument und leert daher nur this.promptInput
    // (BotBasedComponent), nicht das an das Eingabefeld gebundene this.myWords - deshalb hier explizit leeren.
    public async sendMyWords(): Promise<void> {
        const text = this.myWords;
        this.myWords = '';
        this.turnLatency.startTextTurn();
        await this.add(text);
    }

    public getOverWriteText(): string {
        return window.avatarbot_overrideText ?? '';
    }

    // Unterscheidet "nie gesetzt" (Standard-Status anzeigen) von "explizit leer gesetzt" (nichts anzeigen)
    public hasOverrideText(): boolean {
        return window.avatarbot_overrideText !== undefined;
    }

    /** Wird vom <video>-Element bei loadedmetadata aufgerufen */
    public onVideoLoadedMetadata(): void {
        const video = this.videoElement?.nativeElement;
        if (video && video.videoWidth > 0 && video.videoHeight > 0) {
            this.aspectRatio = video.videoWidth / video.videoHeight;
            this.resizeDiv();
        }
        this.tryStartVirtualBackground();
    }

    /** Wird vom <video>-Element bei playing aufgerufen */
    public onVideoPlaying(): void {
        this.tryStartVirtualBackground();
    }

    // ─── Responsive Sizing ───────────────────────────────────────────────────

    private resizeDiv(): void {
        // Im Fullscreen-Modus wird relativ zur Content-Flaeche (nicht zum Fenster) skaliert, mit kleinerem
        // Fuellfaktor je nach Ausrichtung, damit der Avatar sichtbar nicht die komplette Flaeche ausfuellt.
        const contentEl = this.fullscreenActive ? this.fullscreenContentEl?.nativeElement : undefined;
        const basisWidth = contentEl?.clientWidth || window.innerWidth;
        const basisHeight = contentEl?.clientHeight || window.innerHeight;
        const fillRatio = contentEl ? (window.matchMedia('(orientation: portrait)').matches ? 0.95 : 0.9) : 0.8;

        const maxWidth = Math.max(basisWidth * fillRatio, this.minVideoWidth);
        const maxHeight = Math.max(basisHeight * fillRatio, this.minVideoHeight);

        let width = maxWidth;
        let height = Math.max(width / this.aspectRatio, this.minVideoHeight);

        if (height > maxHeight) {
            height = Math.max(maxHeight, this.minVideoHeight);
            width = Math.max(height * this.aspectRatio, this.minVideoWidth);
        }

        const px = (n: number) => `${String(n)}px`;

        if (this.videoElement?.nativeElement) {
            this.videoElement.nativeElement.style.width = px(width);
            this.videoElement.nativeElement.style.height = px(height);
        }
        if (this.backgroundImageEl?.nativeElement) {
            this.backgroundImageEl.nativeElement.style.width = px(width);
            this.backgroundImageEl.nativeElement.style.height = px(height);
        }
        if (this.canvasElement?.nativeElement) {
            this.canvasElement.nativeElement.style.width = px(width);
            this.canvasElement.nativeElement.style.height = px(height);
        }
        if (this.videoWrapper?.nativeElement) {
            this.videoWrapper.nativeElement.style.width = px(width);
            this.videoWrapper.nativeElement.style.height = px(height);
        }

        this.mediaWidth = width;
        this.mediaHeight = height;
    }

    // ─── Virtual Background / Greenscreen ────────────────────────────────────

    // Startet die Live-Chroma-Key-Wiedergabe des D-ID Idle-Videos in der statischen Vorschau vor
    // Verbindungsaufbau (siehe Template) — spielt das Video direkt ab, statt ein Vorschaubild zu
    // generieren/zwischenzuspeichern.
    public startChromaKeyLoop(video: HTMLVideoElement, canvas: HTMLCanvasElement): void {
        startChromaKeyVideoLoop(video, canvas);
    }


    private tryStartVirtualBackground(): void {
        if (this.viewState !== 'CONNECTED' || this.isVirtualBgRunning || !this.effectiveGreenscreen) return;

        const canvas = this.canvasElement?.nativeElement;
        const video = this.videoElement?.nativeElement;

        if (!canvas) {
            this.scheduleVirtualBgRetry();
            return;
        }
        if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
            this.scheduleVirtualBgRetry();
            return;
        }

        if (this.virtualBgRetryTimeoutId !== null) {
            clearTimeout(this.virtualBgRetryTimeoutId);
            this.virtualBgRetryTimeoutId = null;
        }
        this.virtualBgRetryCount = 0;
        this.isVirtualBgRunning = true;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        this.calcVirtualBg();
    }

    private scheduleVirtualBgRetry(): void {
        if (this.isVirtualBgRunning || this.virtualBgRetryCount >= this.maxVirtualBgRetries || !this.effectiveGreenscreen) return;
        if (this.virtualBgRetryTimeoutId !== null) return;

        this.virtualBgRetryCount++;
        this.virtualBgRetryTimeoutId = window.setTimeout(() => {
            this.virtualBgRetryTimeoutId = null;
            this.tryStartVirtualBackground();
        }, 200);
    }

    private stopVirtualBackground(): void {
        this.isVirtualBgRunning = false;
        this.virtualBgRetryCount = 0;
        if (this.virtualBgAnimationFrameId !== null) {
            cancelAnimationFrame(this.virtualBgAnimationFrameId);
            this.virtualBgAnimationFrameId = null;
        }
        if (this.virtualBgRetryTimeoutId !== null) {
            clearTimeout(this.virtualBgRetryTimeoutId);
            this.virtualBgRetryTimeoutId = null;
        }
    }

    public calcVirtualBg(): void {
        if (!this.isVirtualBgRunning) return;
        try {
            const video = this.videoElement.nativeElement;
            const canvas = this.canvasElement.nativeElement;
            if (!canvas || !video) {
                this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
                return;
            }
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) {
                this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
                return;
            }
            if (video.videoWidth === 0 || video.videoHeight === 0) {
                this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
                return;
            }

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            applyChromaKey(imageData);
            ctx.putImageData(imageData, 0, 0);
        } catch (_e) {
            console.warn('[DIdAvatarComponent] Virtual background error', _e);
        }
        this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
    }

    public override async ngOnDestroy(): Promise<void> {
        this.turnLatency.disable();
        await this.disconnectDIdAvatar();
        super.ngOnDestroy();
    }
}
